/**
 * NotificationsPanel — the push editor (spec 2026-08-31 Part B). Replaces the
 * static delay-variant AutomationMomentCard on the flight canvas with an
 * EDITABLE plan: the three boarding reminders (on/off, timing, copy) and the
 * delay-moment copy. Saved WITH the design via useDesignDoc's notifications
 * working copy — this component only renders/edits, the hook owns persistence.
 *
 * Editing model: `value === null` means the built-in plan (the server ignores
 * a NULL notifications_json byte-for-byte); the panel then SHOWS the defaults
 * and the first edit materializes the full document. The reset button hands
 * back null. Hard limits (header 60 / body 400 / minutesBefore 1..43200) are
 * enforced via input constraints; the 35-minute mark-spacing rule stays
 * server-side (a violating save surfaces as "Mentés sikertelen").
 */
import { useT } from '../../i18n';
import type { NotificationSettings, ReminderKey, ReminderSetting } from '../../types';

export interface NotificationsPanelProps {
  /** The working copy (useDesignDoc). Null ⇒ built-in plan. */
  value: NotificationSettings | null;
  /** The server's built-in plan (doc.notificationDefaults). */
  defaults: NotificationSettings;
  onChange: (n: NotificationSettings | null) => void;
}

const REMINDER_TITLE_KEY = {
  t24h: 'notif.t24h',
  t4h: 'notif.t4h',
  t30m: 'notif.t30m',
} as const satisfies Record<ReminderKey, string>;

export function NotificationsPanel({
  value,
  defaults,
  onChange,
}: NotificationsPanelProps): JSX.Element {
  const t = useT();
  const effective = value ?? defaults;

  const patchReminder = (key: ReminderKey, patch: Partial<ReminderSetting>): void => {
    onChange({
      ...effective,
      reminders: effective.reminders.map((r) => (r.key === key ? { ...r, ...patch } : r)),
    });
  };
  const patchDelay = (patch: Partial<NotificationSettings['delayMoment']>): void => {
    onChange({ ...effective, delayMoment: { ...effective.delayMoment, ...patch } });
  };

  return (
    <section className="card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800">
          <span aria-hidden="true">🔔</span> {t('notif.title')}
        </h3>
        {value === null ? (
          <span className="text-xs rounded-full bg-slate-100 px-2 py-0.5 text-slate-500">
            {t('notif.defaultBadge')}
          </span>
        ) : (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-xs text-slate-500 underline"
          >
            {t('notif.reset')}
          </button>
        )}
      </div>
      <p className="text-xs text-slate-500">{t('notif.hint')}</p>

      {effective.reminders.map((r) => {
        const title = t(REMINDER_TITLE_KEY[r.key]);
        return (
          <div key={r.key} className="rounded-md ring-1 ring-slate-200 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-700">{title}</p>
              <label className="flex items-center gap-1 text-xs text-slate-600">
                <input
                  type="checkbox"
                  aria-label={`${title} — ${t('notif.enabled')}`}
                  checked={r.enabled}
                  onChange={(e) => patchReminder(r.key, { enabled: e.target.checked })}
                />
                {t('notif.enabled')}
              </label>
            </div>
            <label className="block text-xs text-slate-600">
              {t('notif.minutesBefore')}
              <input
                type="number"
                aria-label={`${title} — ${t('notif.minutesBefore')}`}
                min={1}
                max={1440}
                value={r.minutesBefore}
                disabled={!r.enabled}
                onChange={(e) => {
                  // Clearing the field yields Number('') === 0 — never emit a
                  // sub-1 mark the server would 422 (keep the last valid value
                  // instead); over-cap input clamps to the sweep's honest max.
                  const n = Number(e.target.value);
                  if (Number.isFinite(n) && n >= 1) {
                    patchReminder(r.key, { minutesBefore: Math.min(Math.round(n), 1440) });
                  }
                }}
                className="input mt-1"
              />
            </label>
            <label className="block text-xs text-slate-600">
              {t('notif.header')}
              <input
                type="text"
                aria-label={`${title} — ${t('notif.header')}`}
                maxLength={60}
                value={r.header}
                disabled={!r.enabled}
                onChange={(e) => patchReminder(r.key, { header: e.target.value })}
                className="input mt-1"
              />
            </label>
            <label className="block text-xs text-slate-600">
              {t('notif.body')}
              <textarea
                aria-label={`${title} — ${t('notif.body')}`}
                maxLength={400}
                rows={3}
                value={r.body}
                disabled={!r.enabled}
                onChange={(e) => patchReminder(r.key, { body: e.target.value })}
                className="input mt-1"
              />
            </label>
          </div>
        );
      })}

      <div className="rounded-md ring-1 ring-amber-200 bg-amber-50 p-3 space-y-2">
        <p className="text-sm font-medium text-amber-900">
          <span aria-hidden="true">⚡</span> {t('moment.title')}
        </p>
        <p className="text-xs text-amber-800">{t('notif.delayHint')}</p>
        <label className="block text-xs text-amber-900">
          {t('notif.header')}
          <input
            type="text"
            aria-label={`${t('moment.title')} — ${t('notif.header')}`}
            maxLength={60}
            value={effective.delayMoment.header}
            onChange={(e) => patchDelay({ header: e.target.value })}
            className="input mt-1 ring-amber-300"
          />
        </label>
        <label className="block text-xs text-amber-900">
          {t('notif.body')}
          <textarea
            aria-label={`${t('moment.title')} — ${t('notif.body')}`}
            maxLength={400}
            rows={3}
            value={effective.delayMoment.body}
            onChange={(e) => patchDelay({ body: e.target.value })}
            className="input mt-1 ring-amber-300"
          />
        </label>
      </div>
    </section>
  );
}
